import React, { useCallback, useMemo, useState } from 'react';
import {
  useCreateInspectionStatusAccessRule,
  useDeleteInspectionStatusAccessRule,
  useInspectionStatusAccessRules,
  useInspectionStatuses,
  useUpdateInspectionStatusAccessRule,
} from '@/api/InspectionStatus';
import { useCustomersHierarchy } from '@/api/Customers';
import { queryClient } from '@/config/reactQuery';
import { QUERY_KEYS } from '@/api/queryKeys';
import {
  CreateInspectionStatusAccessRule,
  InspectionStatusAccessRule,
  UpdateInspectionStatusAccessRule,
} from '@/types';
import { App, Button, Space, Switch } from 'antd';
import {
  CustomColumns,
  CustomTable,
  faEdit,
  faInfoCircle,
  faTrash,
  Icon,
} from '@/ui/components';
import { useTranslate } from '@/translations';
import { AccessRulePopup } from './AccessRulePopup';
import { useUserTypes } from '@/api/Users';
import { getConditionGroupLabel } from '../constants';

const emptyRule = (): InspectionStatusAccessRule => ({
  id: 0,
  customerId: 0,
  subCustomerId: 0,
  inspectionStatusId: undefined as unknown as number,
  userType: 'InternalUsers',
  userTypeName: 'Internal User',
  canView: true,
  canEditDamage: false,
  canEditFr: false,
  canEditParts: false,
  canUpdateDamageStatus: false,
  canChangePartComponent: false,
  canChangeDamageType: false,
  canChangeSeverity: false,
  canChangeRepairMethod: false,
  canChangeWorkCenter: false,
  canRemoveDamageLine: false,
  isActive: true,
  nextStatuses: [],
});

export const AccessRulesDataGrid: React.FC = () => {
  const { t } = useTranslate();
  const { modal } = App.useApp();

  const { data: rules, isFetching: isRulesLoading } =
    useInspectionStatusAccessRules();
  const { data: customersHierarchy, isFetching: isHierarchyLoading } =
    useCustomersHierarchy();
  const { data: inspectionStatuses, isFetching: isStatusesLoading } =
    useInspectionStatuses();
  const { data: userTypes, isFetching: isUserTypesLoading } = useUserTypes();

  const { mutateAsync: createMutate } = useCreateInspectionStatusAccessRule();
  const { mutateAsync: updateMutate } = useUpdateInspectionStatusAccessRule();
  const { mutateAsync: deleteMutate } = useDeleteInspectionStatusAccessRule();

  const [rule, setRule] = useState<InspectionStatusAccessRule>();

  const customerOptions = useMemo(() => {
    const fromHierarchy = (customersHierarchy || []).map((c) => ({
      label: c.customerName,
      value: c.customerId,
    }));
    return [{ label: '-', value: 0 }, ...fromHierarchy];
  }, [customersHierarchy]);

  const customerFilterOptions = useMemo(
    () =>
      customerOptions.map((o) => ({
        text: o.label,
        value: o.value,
      })),
    [customerOptions],
  );

  const subCustomerFilterOptions = useMemo(() => {
    const options: { text: string; value: number }[] = [
      { text: 'Unscoped', value: 0 },
    ];
    (customersHierarchy || []).forEach((parent) => {
      parent.children?.forEach((child) => {
        options.push({
          text: child.customerName,
          value: child.customerId,
        });
      });
    });
    return options;
  }, [customersHierarchy]);

  const statusFilterOptions = useMemo(
    () =>
      (inspectionStatuses || []).map((s) => ({
        text: s.text,
        value: s.value,
      })),
    [inspectionStatuses],
  );

  const statusOptions = useMemo(
    () =>
      (inspectionStatuses || []).map((s) => ({
        label: s.text,
        value: s.value,
      })),
    [inspectionStatuses],
  );

  const userTypeFilterOptions = useMemo(
    () =>
      userTypes?.map((o) => ({
        text: o.name,
        value: o.value,
      })),
    [userTypes],
  );

  const sortOrderByStatusId = useMemo(() => {
    const map = new Map<number, number>();
    (inspectionStatuses || []).forEach((s) => {
      map.set(s.value, s.inspectionSortOrder);
    });
    return map;
  }, [inspectionStatuses]);

  const getStatusSortOrder = useCallback(
    (rule: InspectionStatusAccessRule) =>
      rule.inspectionSortOrder ??
      sortOrderByStatusId.get(rule.inspectionStatusId) ??
      Number.MAX_SAFE_INTEGER,
    [sortOrderByStatusId],
  );

  const compareCustomer = useCallback(
    (a: InspectionStatusAccessRule, b: InspectionStatusAccessRule) => {
      if (a.customerId === 0 && b.customerId !== 0) return -1;
      if (a.customerId !== 0 && b.customerId === 0) return 1;
      return (a.customerName || '').localeCompare(b.customerName || '');
    },
    [],
  );

  const compareSubCustomer = useCallback(
    (a: InspectionStatusAccessRule, b: InspectionStatusAccessRule) => {
      if (a.subCustomerId === 0 && b.subCustomerId !== 0) return -1;
      if (a.subCustomerId !== 0 && b.subCustomerId === 0) return 1;
      return (a.subCustomerName || '').localeCompare(b.subCustomerName || '');
    },
    [],
  );

  const compareStatus = useCallback(
    (a: InspectionStatusAccessRule, b: InspectionStatusAccessRule) =>
      getStatusSortOrder(a) - getStatusSortOrder(b),
    [getStatusSortOrder],
  );

  const compareUserType = useCallback(
    (a: InspectionStatusAccessRule, b: InspectionStatusAccessRule) =>
      (a.userTypeName || '').localeCompare(b.userTypeName || ''),
    [],
  );

  const sortedRules = useMemo(() => {
    const list = [...(rules || [])];
    list.sort((a, b) => {
      const userTypeCmp = compareUserType(a, b);
      if (userTypeCmp !== 0) return userTypeCmp;
      const statusCmp = compareStatus(a, b);
      if (statusCmp !== 0) return statusCmp;
      const customerCmp = compareCustomer(a, b);
      if (customerCmp !== 0) return customerCmp;
      return compareSubCustomer(a, b);
    });
    return list;
  }, [
    compareCustomer,
    compareStatus,
    compareSubCustomer,
    compareUserType,
    rules,
  ]);

  const invalidateRules = () =>
    queryClient.invalidateQueries({
      queryKey: [QUERY_KEYS.inspectionStatusAccessRules],
    });

  const onSubmit = async (data: InspectionStatusAccessRule) => {
    const nextStatuses = (data.nextStatuses || []).map((ns, index) => ({
      nextInspectionStatusId: ns.nextInspectionStatusId,
      conditionGroup: ns.conditionGroup || null,
      sortOrder: index + 1,
    }));

    let res;
    if (data.id) {
      const updateData: UpdateInspectionStatusAccessRule = {
        canView: data.canView,
        canEditDamage: data.canEditDamage,
        canEditFr: data.canEditFr,
        canEditParts: data.canEditParts,
        canUpdateDamageStatus: data.canUpdateDamageStatus,
        canChangePartComponent: data.canChangePartComponent,
        canChangeDamageType: data.canChangeDamageType,
        canChangeSeverity: data.canChangeSeverity,
        canChangeRepairMethod: data.canChangeRepairMethod,
        canChangeWorkCenter: data.canChangeWorkCenter,
        canRemoveDamageLine: data.canRemoveDamageLine,
        isActive: data.isActive,
        nextStatuses,
      };
      res = await updateMutate({ id: data.id, data: updateData });
    } else {
      const createData: CreateInspectionStatusAccessRule = {
        customerId: data.customerId ?? 0,
        subCustomerId: data.subCustomerId ?? 0,
        inspectionStatusId: data.inspectionStatusId,
        userType: data.userType,
        canView: data.canView,
        canEditDamage: data.canEditDamage,
        canEditFr: data.canEditFr,
        canEditParts: data.canEditParts,
        canUpdateDamageStatus: data.canUpdateDamageStatus,
        canChangePartComponent: data.canChangePartComponent,
        canChangeDamageType: data.canChangeDamageType,
        canChangeSeverity: data.canChangeSeverity,
        canChangeRepairMethod: data.canChangeRepairMethod,
        canChangeWorkCenter: data.canChangeWorkCenter,
        canRemoveDamageLine: data.canRemoveDamageLine,
        isActive: data.isActive,
        nextStatuses,
      };
      res = await createMutate(createData);
    }

    if (res.success) await invalidateRules();

    return res.message?.Message;
  };

  const onDelete = useCallback(
    (id: number) => {
      modal.confirm({
        title: t('confirm'),
        content: t('deleteRecordQuestion'),
        okText: t('yes'),
        onOk: async () => {
          await deleteMutate(id);
          await invalidateRules();
        },
      });
    },
    [deleteMutate, modal, t],
  );

  const columns: CustomColumns<InspectionStatusAccessRule> = useMemo(
    () => [
      {
        dataIndex: 'userType',
        title: t('userType'),
        width: 120,
        sortable: true,
        sorter: compareUserType,
        filter: true,
        renderType: 'select',
        filterOptions: userTypeFilterOptions,
        render: (_, data) => data.userTypeName,
        fixed: 'left',
      },
      {
        dataIndex: 'inspectionStatusId',
        title: t('status'),
        width: 200,
        sortable: true,
        sorter: compareStatus,
        filter: true,
        renderType: 'select',
        filterOptions: statusFilterOptions,
        render: (_, data) =>
          data.inspectionStatusName || data.inspectionStatusId,
        fixed: 'left',
      },
      {
        dataIndex: 'customerId',
        title: t('customer'),
        render: (_, data) =>
          data.customerId === 0 ? '-' : data.customerName || data.customerId,
        width: 120,
        sortable: true,
        sorter: compareCustomer,
        filter: true,
        renderType: 'select',
        filterOptions: customerFilterOptions,
      },
      {
        dataIndex: 'subCustomerId',
        title: t('subCustomer'),
        render: (_, data) =>
          data.subCustomerId === 0
            ? '-'
            : data.subCustomerName || data.subCustomerId,
        width: 150,
        sortable: true,
        sorter: compareSubCustomer,
        filter: true,
        renderType: 'select',
        filterOptions: subCustomerFilterOptions,
      },
      {
        dataIndex: 'canView',
        title: t('canView'),
        width: 140,
        align: 'center',
        render: (value) => <Switch checked={value} disabled />,
      },
      {
        dataIndex: 'canEditDamage',
        title: t('canEditDamage'),
        width: 120,
        align: 'center',
        render: (value) => <Switch checked={value} disabled />,
      },
      {
        dataIndex: 'canEditFr',
        title: t('canEditFr'),
        width: 110,
        align: 'center',
        render: (value) => <Switch checked={value} disabled />,
      },
      {
        dataIndex: 'canEditParts',
        title: t('canEditParts'),
        width: 110,
        align: 'center',
        render: (value) => <Switch checked={value} disabled />,
      },
      {
        dataIndex: 'canUpdateDamageStatus',
        title: t('canUpdateDamageStatus'),
        width: 160,
        align: 'center',
        render: (value) => <Switch checked={value} disabled />,
      },
      {
        dataIndex: 'canChangePartComponent',
        title: t('canChangePartComponent'),
        width: 180,
        align: 'center',
        render: (value) => <Switch checked={value} disabled />,
      },
      {
        dataIndex: 'canChangeDamageType',
        title: t('canChangeDamageType'),
        width: 180,
        align: 'center',
        render: (value) => <Switch checked={value} disabled />,
      },
      {
        dataIndex: 'canChangeSeverity',
        title: t('canChangeSeverity'),
        width: 150,
        align: 'center',
        render: (value) => <Switch checked={value} disabled />,
      },
      {
        dataIndex: 'canChangeRepairMethod',
        title: t('canChangeRepairMethod'),
        width: 190,
        align: 'center',
        render: (value) => <Switch checked={value} disabled />,
      },
      {
        dataIndex: 'canChangeWorkCenter',
        title: t('canChangeWorkCenter'),
        width: 180,
        align: 'center',
        render: (value) => <Switch checked={value} disabled />,
      },
      {
        dataIndex: 'canRemoveDamageLine',
        title: t('canRemoveDamageLine'),
        width: 150,
        align: 'center',
        render: (value) => <Switch checked={value} disabled />,
      },
      {
        dataIndex: 'isActive',
        title: t('active'),
        width: 90,
        align: 'center',
        render: (value) => <Switch checked={value} disabled />,
      },
      {
        dataIndex: 'nextStatuses',
        title: t('nextStatuses'),
        width: 220,
        render: (_, data) => (
          <Space direction="vertical">
            {(data.nextStatuses || []).map((ns) => (
              <div>
                {ns.nextInspectionStatusName || ns.nextInspectionStatusId}
                {ns.conditionGroup
                  ? ` (${getConditionGroupLabel(ns.conditionGroup)})`
                  : ''}
              </div>
            ))}
          </Space>
        ),
      },
      {
        dataIndex: 'id',
        title: t('actions'),
        width: 200,
        fixed: 'right',
        render: (_, data) => (
          <Space>
            <Button onClick={() => setRule({ ...data })}>
              <Icon icon={faEdit} /> {t('edit')}
            </Button>
            <Button danger onClick={() => onDelete(data.id)}>
              <Icon icon={faTrash} /> {t('delete')}
            </Button>
          </Space>
        ),
      },
    ],
    [
      compareCustomer,
      compareStatus,
      compareSubCustomer,
      compareUserType,
      customerFilterOptions,
      onDelete,
      statusFilterOptions,
      subCustomerFilterOptions,
      t,
      userTypeFilterOptions,
    ],
  );

  const isLoading =
    isRulesLoading ||
    isHierarchyLoading ||
    isStatusesLoading ||
    isUserTypesLoading;

  return (
    <>
      <CustomTable
        dataSource={sortedRules}
        columns={columns}
        rowKey="id"
        loading={isLoading}
        onAdd={() => setRule(emptyRule())}
        headerActions={{
          left: (
            <span>
              <Icon icon={faInfoCircle} className="me-1" />
              Internal super admin users have access to all the access listed
              below.
            </span>
          ),
        }}
        scroll={{ x: 1800 }}
      />

      <AccessRulePopup
        data={rule}
        customerOptions={customerOptions}
        customersHierarchy={customersHierarchy || []}
        statusOptions={statusOptions}
        userTypeOptions={
          userTypes?.map((ut) => ({
            label: ut.name,
            value: ut.value,
          })) || []
        }
        onCancel={() => setRule(undefined)}
        onSubmit={onSubmit}
      />
    </>
  );
};
